import type { PimWorkflowReceipt, PimWorkflowScope } from "@nova-ai/shared"
import { ChevronDown } from "lucide-react"
import { formatDateTime } from "../../lib/format-date"
import { WorkflowStage } from "./workflow-history-evidence"
import {
  hasNoDownstreamHistory,
  scopeFailures,
  WORKFLOW_STAGES,
  workflowKindLabel,
} from "./workflow-history-presentation"

type Props = { members: PimWorkflowScope[]; receipt: PimWorkflowReceipt }

export function WorkflowHistoryProgress({ members, receipt }: Props) {
  const first = members[0]
  if (!first) {
    return null
  }
  const failures = scopeFailures(first)
  const stores = [...new Set(members.map((member) => member.store))]
  const isMissing = hasNoDownstreamHistory(first)
  const isIncomplete =
    !isMissing && WORKFLOW_STAGES.some(([key]) => first[key].evidence === "unknown")
  return (
    <details
      open={failures.length > 0}
      aria-label={`Store progress: ${stores.join(", ")} · ${first.sku ?? "SKU unresolved"} · ${workflowKindLabel(first.kind)}`}
      className="group rounded-xl border border-border bg-card shadow-card"
    >
      <summary className="flex cursor-pointer list-none items-start gap-3 rounded-xl p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-details-marker]:hidden">
        <ChevronDown
          aria-hidden="true"
          className="mt-0.5 size-4 shrink-0 -rotate-90 text-muted-foreground transition-transform group-open:rotate-0 motion-reduce:transition-none"
        />

        <div className="min-w-0 flex-1 space-y-1">
          <p className="break-words text-sm font-semibold">{stores.join(", ")}</p>

          <p className="break-words text-xs text-muted-foreground">
            {`${first.sku ?? "SKU unresolved"} · ${workflowKindLabel(first.kind)} · ${stores.length} displayed ${stores.length === 1 ? "store" : "stores"}`}
          </p>

          {failures.map(({ label, reason }) => (
            <p key={label} className="text-sm text-destructive">
              <span className="font-medium">{`${label} failed: `}</span>

              {reason}
            </p>
          ))}

          <RecoveryNote recovery={first.recovery} showTime={members.length === 1} />

          {failures.length === 0 && (
            <p className="text-xs text-muted-foreground">View steps and recorded times</p>
          )}
        </div>
      </summary>

      <div className="space-y-4 px-4 pb-4">
        <ol
          aria-label="Workflow steps"
          className="grid gap-4 border-t border-border pt-4 lg:grid-cols-6"
        >
          <li aria-label="Received" className="min-w-0 space-y-2">
            <p className="text-sm font-medium">Received</p>

            <WorkflowStage
              stage={{
                state: receipt.validation === "accepted" ? "complete" : "failed",
                reason: receipt.validationReason,
                recordedAt: receipt.receivedAt,
                evidence: "recorded",
              }}
            />
          </li>

          <li aria-label="Content" className="min-w-0 space-y-3">
            <p className="text-sm font-medium">Content</p>

            <DispatchStage scope={first} showTime={members.length === 1} />

            {!isMissing && <WorkflowStage stage={first.content} showTime={members.length === 1} />}
          </li>

          {!isMissing &&
            WORKFLOW_STAGES.filter(([key]) => key !== "dispatch" && key !== "content").map(
              ([key, label]) => (
                <li key={key} aria-label={label} className="min-w-0 space-y-2">
                  <p className="text-sm font-medium">{label}</p>

                  <WorkflowStage stage={first[key]} showTime={members.length === 1} />
                </li>
              )
            )}
        </ol>

        {isMissing && (
          <p className="text-sm text-muted-foreground">Detailed step history was not recorded.</p>
        )}

        {isIncomplete && (
          <p className="text-xs text-muted-foreground">Some step history is incomplete.</p>
        )}

        {members.length > 1 && <StoreTimes members={members} />}
      </div>
    </details>
  )
}

function RecoveryNote({
  recovery,
  showTime,
}: {
  recovery: PimWorkflowScope["recovery"]
  showTime: boolean
}) {
  if (!recovery?.resolvedAt) {
    return null
  }
  return (
    <p className="text-xs text-muted-foreground">
      Recovery verified. Earlier failed steps remain historical.
      {showTime && (
        <time className="ml-1" dateTime={recovery.resolvedAt}>
          {formatDateTime(recovery.resolvedAt)}
        </time>
      )}
    </p>
  )
}

function DispatchStage({ scope, showTime }: { scope: PimWorkflowScope; showTime: boolean }) {
  const labels: Record<PimWorkflowScope["dispatch"]["state"], string> = {
    queued: "Waiting to start",
    running: "Starting",
    retrying: "Retrying start",
    failed: "Unable to start",
    complete: "Started",
    blocked: "Start blocked",
    not_applicable: "Start not applicable",
    superseded: "Start superseded",
    unknown: "Start unknown",
  }
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">{labels[scope.dispatch.state]}</p>

      <WorkflowStage stage={scope.dispatch} showTime={showTime} />
    </div>
  )
}

function StoreTimes({ members }: { members: PimWorkflowScope[] }) {
  return (
    <details className="border-t border-border pt-3">
      <summary className="cursor-pointer rounded text-xs text-info focus-visible:ring-2 focus-visible:ring-ring">
        Recorded times by store
      </summary>

      <div className="mt-3 space-y-4">
        {members.map((member) => (
          <div key={member.id} className="space-y-2">
            <p className="break-words text-sm font-medium">{member.store}</p>

            <dl className="flex flex-wrap gap-x-6 gap-y-2">
              {member.recovery?.resolvedAt && (
                <div className="text-xs">
                  <dt className="text-muted-foreground">Recovery verified</dt>

                  <dd>
                    <time dateTime={member.recovery.resolvedAt}>
                      {formatDateTime(member.recovery.resolvedAt)}
                    </time>
                  </dd>
                </div>
              )}

              {WORKFLOW_STAGES.map(
                ([key, label]) =>
                  member[key].recordedAt && (
                    <div key={key} className="text-xs">
                      <dt className="text-muted-foreground">{label}</dt>

                      <dd>
                        <time dateTime={member[key].recordedAt}>
                          {formatDateTime(member[key].recordedAt)}
                        </time>
                      </dd>
                    </div>
                  )
              )}
            </dl>

            {!WORKFLOW_STAGES.some(([key]) => member[key].recordedAt) && (
              <p className="text-xs text-muted-foreground">No step times recorded.</p>
            )}
          </div>
        ))}
      </div>
    </details>
  )
}
